import { GetStaticPaths, GetStaticProps } from 'next';
import React from 'react';

import { getServerPageGetRoadMaintenanceDetail, getServerPageGetRoadMaintenancesSlugs } from 'api/graphql-helper';
import client, { httpLink, setPreviewSecret } from 'api/apollo-client';
import {
	RoadMaintenanceFieldsFragment,
	RoadMaintenanceDetailSingleFieldsFragment,
	PageInformationFieldsFragment,
	Enum_Componentinternaldefaultmoduleoptions_Backgroundcolor,
	Enum_Componentinternaldefaultmoduleoptions_Padding
} from 'api/graphql-types';

import Layout from 'modules/layout/layout';
import { ModuleBuilder } from 'modules/module-builder/module-builder';
import { Head } from 'modules/head/head';
import IntroHeader from 'modules/intro-header/intro-header';
import { RoadMaintenanceHeader } from 'modules/road-maintenance-header/road-maintenance-header';
import { BaseModule } from 'modules/base-module/base-module';

import { getStrapiParametersFromContext } from 'utils/strapi-parameters';
import { IBasePageProps } from 'types/page';
import usePageProtection, { IUsePageProtectionVariables } from 'utils/hooks/use-page-protection';
import PasswordLayout from 'modules/password-layout';
import { buildPasswordProtectionStaticProps } from 'utils/password-protection-static';
import { ROUTES } from 'constants/routes';
import { buildSlugPaths } from 'utils/build-slug-paths';

interface IRoadMaintenanceDetailPageProps extends IBasePageProps {
	pageInformation?: PageInformationFieldsFragment;
	roadMaintenanceDetail?: RoadMaintenanceDetailSingleFieldsFragment;
	roadMaintenanceBySlug?: RoadMaintenanceFieldsFragment;
	id?: string;
	variables?: IUsePageProtectionVariables;
	isPasswordProtected?: boolean;
	passwordPageLayout?: any;
}

const RoadMaintenanceDetailPage = (props: IRoadMaintenanceDetailPageProps): React.JSX.Element => {
	const { page, PasswordModule } = usePageProtection({
		pageData: props.roadMaintenanceBySlug || null,
		pageQuery: () => getServerPageGetRoadMaintenanceDetail({ variables: props.variables }, client)
	});

	const modules = page?.modules || [];
	const globalModules = props.roadMaintenanceDetail?.modules || [];
	const slug = page?.slug;

	return (
		<Layout
			contentId={props?.id}
			typeName={page?.__typename}
			navbar={props?.pageInformation?.navbar}
			footer={props?.pageInformation?.footer}
		>
			<Head seoModule={page?.seo} seoParent={props?.pageInformation?.seo} />
			{page && page.__typename === 'RoadMaintenance' ? (
				<>
					<BaseModule
						defaultModuleOptions={{
							backgroundColor: page.header
								?.backgroundColor as Enum_Componentinternaldefaultmoduleoptions_Backgroundcolor,
							padding: 'none' as Enum_Componentinternaldefaultmoduleoptions_Padding
						}}
					>
						<IntroHeader module={page.header} slug={slug} />
					</BaseModule>
					<RoadMaintenanceHeader module={page} />
					<ModuleBuilder modules={[...modules, ...globalModules]} slug={slug || '/'} />
				</>
			) : props.isPasswordProtected ? (
				<PasswordLayout
					locale={props.variables?.locale}
					slug={props.variables?.slug}
					pageData={props.passwordPageLayout}
				>
					<PasswordModule />
				</PasswordLayout>
			) : null}
		</Layout>
	);
};

export const getStaticPaths: GetStaticPaths = async (ctx) => {
	const result = await getServerPageGetRoadMaintenancesSlugs({}, client);
	const slugs = result.props.data.findAllSlugs;

	return {
		paths: buildSlugPaths(slugs, ROUTES.ROAD_MAINTENANCES.slug, ctx.defaultLocale),
		fallback: 'blocking'
	};
};

export const getStaticProps: GetStaticProps = async (ctx) => {
	try {
		const { locale, draftMode, publicationState } = getStrapiParametersFromContext(ctx);
		const slug = ctx.params?.slug ? `/${ROUTES.ROAD_MAINTENANCES.slug}/${ctx.params.slug}` : '/';
		const variables = { slug, locale, publicationState };

		if (publicationState === 'DRAFT') {
			client.setLink(setPreviewSecret(process.env.STRAPI_PREVIEW_SECRET as string).concat(httpLink));
		}

		const passwordResult = await buildPasswordProtectionStaticProps<any, any, any>({
			fullSlug: slug,
			locale,
			publicationState,
			draftMode,
			contentTypeUid: 'api::road-maintenance.road-maintenance',
			contentTypeName: 'road-maintenance',
			fetchPage: () => getServerPageGetRoadMaintenanceDetail({ variables }, client),
			extractInformation: (data) => data?.props?.data?.pageInformation?.data?.attributes,
			extractPage: (data) => {
				const node = data?.props?.data?.roadMaintenanceBySlug?.data;
				return node ? { documentId: node.documentId, attributes: node.attributes } : null;
			}
		});

		let roadMaintenanceDetail = null;
		if (!passwordResult.isPasswordProtected && passwordResult.page) {
			const detail = await getServerPageGetRoadMaintenanceDetail({ variables }, client);
			roadMaintenanceDetail = detail.props.data?.roadMaintenanceDetail?.data?.attributes || null;
		}

		if (!passwordResult.isPasswordProtected && !passwordResult.page) {
			return { notFound: true };
		}

		return {
			props: {
				id: passwordResult.page?.documentId || null,
				variables,
				pageInformation: passwordResult.pageInformation,
				roadMaintenanceDetail: roadMaintenanceDetail,
				roadMaintenanceBySlug: passwordResult.page,
				isPasswordProtected: passwordResult.isPasswordProtected,
				passwordPageLayout: passwordResult.passwordPageLayout,
				initialReduxState: passwordResult.initialReduxState
			},
			revalidate: Number(process.env.NEXT_PUBLIC_REVALIDATE_TIME_IN_SECONDS)
		};
	} catch (error: any) {
		// eslint-disable-next-line no-console
		console.error('Error in road maintenance', error);
		return { notFound: true };
	}
};

export default RoadMaintenanceDetailPage;
